import React, { useState, useEffect } from "react";

import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";
import { Separator } from "../../ui/separator";
import { setAlert } from "../../../actions/alert";
import { connect } from "react-redux";
import api from "../../../api/api";
import { ReactComponent as TitleIcon } from "../../../assets/icons/svg/scale-unbalanced-solid.svg";
import PropTypes from "prop-types";
import { Typography, Button } from "@mui/material";
import { Link } from "react-router-dom";

import { Helmet } from "react-helmet";

const UNIT_DOMAINS = [
  {
    key: "measurementPreference",
    label: "Measurement Preference",
    options: [
      { value: "weight", label: "Weight (e.g. grams)" },
      { value: "volume", label: "Volume (e.g. cups)" },
    ],
  },
  {
    key: "weight",
    label: "Weight",
    options: [
      { value: "metric", label: "Metric (g, kg)" },
      { value: "imperial", label: "Imperial (oz, lb)" },
    ],
  },
  {
    key: "volume",
    label: "Volume",
    options: [
      { value: "metric", label: "Metric (ml, l)" },
      { value: "imperial", label: "Imperial (fl oz, cup, qt)" },
    ],
  },
  {
    key: "cupType",
    label: "Cup Type",
    options: [
      { value: "US", label: "US Cup (236.588 mL)" },
      { value: "metric", label: "Metric Cup (250 mL)" },
    ],
  },
  {
    key: "length",
    label: "Length",
    options: [
      { value: "metric", label: "Metric (cm, mm)" },
      { value: "imperial", label: "Imperial (in, ft)" },
    ],
  },
  {
    key: "temperature",
    label: "Temperature",
    options: [
      { value: "fahrenheit", label: "Fahrenheit (°F)" },
      { value: "celsius", label: "Celsius (°C)" },
    ],
  },
];

export const UnitPreferences = ({ setAlert }) => {
  const [units, setUnits] = useState({
    measurementPreference: "weight",
    weight: "metric",
    volume: "metric",
    cupType: "US",
    length: "metric",
    temperature: "fahrenheit",
  });
  const [saving, setSaving] = useState(false);
  const [showCupType, setShowCupType] = useState(false);
  const [cupSubtext, setCupSubtext] = useState("236.588 mL");

  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const res = await api.get("/settings/units");
        setUnits(res.data);
      } catch {
        setAlert("Failed to load unit preferences", "error");
      }
    };
    fetchUnits();
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (domainKey, value) => {
    if (domainKey === "volume") {
      setShowCupType(value === "metric");
      setCupSubtext(value === "metric" ? "250 mL" : "236.588 mL");
    }
    setUnits((prev) => ({ ...prev, [domainKey]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/settings/units", units);
      setAlert("Unit preferences saved", "success");
    } catch {
      setAlert("Error saving unit preferences", "error");
    }
    setSaving(false);
  };

  return (
    <div className="my-[4rem] md:my-0 md:pt-6 bg-neutral-50 min-h-device">
      <Helmet>
        <title>Unit Preferences</title>
      </Helmet>
      <div className="w-full md:pl-[5rem]">
        <div className="px-4 py-3 md:px-8 md:py-6 md:mx-auto w-full md:max-w-[600px] border-b md:border border-separator md:rounded-[16px] bg-background">
          <div className="flex flex-col">
            <div className="my-4 md:mt-0">
              <Link className="hover:bg-transparent text-secondaryText p-0 justify-start" to="/settings">
                <Typography variant="body2" className="body1 leading-none">{`< Go Back`}</Typography>
              </Link>
            </div>
            <div className="flex flex-row">
              <div className="mr-4">
                <TitleIcon className="h-[1.25rem] fill-primaryText"></TitleIcon>
              </div>
              <div className="mb-4">
                <Typography variant="h6" className="font-normal leading-none mb-1">
                  Unit Preferences
                </Typography>
                <Typography variant="subtitle2" className="text-secondaryText leading-tight font-normal">
                  Choose your preferred units for recipes
                </Typography>
              </div>
            </div>
          </div>
          <Separator className="my-4" />
          <div className="flex flex-col gap-6">
            {UNIT_DOMAINS.map(({ key, label, options, reference }) => (
              <div key={key} className="flex flex-col gap-2 w-full">
                <Typography variant="subtitle1">{label}</Typography>
                <Tabs value={units[key]} onValueChange={(value) => handleChange(key, value)} className="w-full">
                  <TabsList className="w-full border border-separator p-0">
                    {options.map((opt) => (
                      <TabsTrigger key={opt.value} value={opt.value} className="w-full h-full">
                        {opt.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>
            ))}
          </div>
          <div></div>
          <Button
            className="mt-8"
            disabled={saving}
            onClick={handleSave}
            variant="contained"
            fullWidth
            color="primary"
            disableElevation
            disableRipple
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

UnitPreferences.propTypes = {
  setAlert: PropTypes.func.isRequired,
};

export default connect(null, { setAlert })(UnitPreferences);
